/**
 * Account settings modal — self-service for any logged-in Aether user
 * (customer / partner / admin): edit display name + change password.
 *
 *  - name        → PATCH /api/aether/auth/me
 *  - password    → POST  /api/aether/auth/me/change-password
 *                  (verifies the current pw, logs out other sessions, and the
 *                   auth context stores the re-issued token so THIS one stays)
 *
 * E-mail is shown read-only (changing it touches the JWT + verification, so
 * that's deliberately left to an admin for now).
 */
import React, { useEffect, useState } from 'react';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAetherAuth } from './AetherAuthContext';

function Msg({ msg }) {
  if (!msg) return null;
  const ok = msg.type === 'ok';
  return (
    <p className={`mt-2 text-xs flex items-center gap-1.5 ${ok ? 'text-emerald-300' : 'text-red-300'}`}>
      {ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {msg.text}
    </p>
  );
}

const inputCls =
  'w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25 placeholder:text-white/25';
const btnCls =
  'px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-black transition hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100';

export default function AccountSettingsModal({ open, onClose }) {
  const { user, updateProfile, requestPasswordReset } = useAetherAuth();

  const [name, setName] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setName(user?.name || '');
    setNameMsg(null); setPwMsg(null);
  }, [open, user]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const saveName = async () => {
    setNameBusy(true); setNameMsg(null);
    try {
      await updateProfile(name.trim());
      setNameMsg({ type: 'ok', text: 'Name gespeichert.' });
    } catch (e) {
      setNameMsg({ type: 'err', text: e.message || 'Fehler beim Speichern.' });
    } finally {
      setNameBusy(false);
    }
  };

  const requestPwReset = async () => {
    setPwBusy(true); setPwMsg(null);
    try {
      await requestPasswordReset(user?.email);
      setPwMsg({ type: 'ok', text: 'E-Mail verschickt — prüfe dein Postfach (ggf. Spam-Ordner).' });
    } catch (e) {
      setPwMsg({ type: 'err', text: e.message || 'Konnte die E-Mail nicht senden.' });
    } finally {
      setPwBusy(false);
    }
  };

  const nameUnchanged = name.trim() === (user?.name || '').trim();

  return (
    <div className="fixed inset-0 z-[190] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Konto-Einstellungen">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative monolith-heavy w-full max-w-md p-6 max-h-[90vh] overflow-y-auto aether-scrollbar">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white text-lg font-medium">Konto-Einstellungen</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white" title="Schließen"><X size={18} /></button>
        </div>

        {/* E-mail (read-only) */}
        <div className="mb-5">
          <label className="block font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1.5">E-Mail</label>
          <div className="px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 text-white/50 text-sm font-mono truncate">{user?.email || '—'}</div>
        </div>

        {/* Name */}
        <div className="mb-6">
          <label className="block font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1.5">Name</label>
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} maxLength={100} />
            <button onClick={saveName} disabled={nameBusy || !name.trim() || nameUnchanged} className={btnCls} style={{ background: 'var(--primary, #F97316)' }}>
              {nameBusy ? <Loader2 size={13} className="animate-spin" /> : 'Speichern'}
            </button>
          </div>
          <Msg msg={nameMsg} />
        </div>

        {/* Password — changed via a secure e-mail link (like the login 2FA),
            never directly in the form. */}
        <div className="pt-5 border-t border-white/5">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">Passwort</p>
          <p className="text-[12px] text-white/50 leading-relaxed mb-3">
            Aus Sicherheitsgründen läuft die Passwort-Änderung über E-Mail: Wir schicken dir einen Link an <span className="text-white/75">{user?.email}</span>, über den du ein neues Passwort setzt.
          </p>
          <Msg msg={pwMsg} />
          <button onClick={requestPwReset} disabled={pwBusy} className={`${btnCls} w-full mt-2`} style={{ background: 'var(--primary, #F97316)' }}>
            {pwBusy ? <span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Sende…</span> : 'Passwort per E-Mail ändern'}
          </button>
        </div>
      </div>
    </div>
  );
}
